import * as Location from 'expo-location';
import * as TaskManager from 'expo-task-manager';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';

const BACKGROUND_LOCATION_TASK = 'background-location-task';
const STORAGE_KEY_TRIP_ID = '@background_trip_id';
const STORAGE_KEY_AUTH_TOKEN = '@auth_token';

const getApiUrl = () => process.env.EXPO_PUBLIC_API_URL
  || Constants.expoConfig?.extra?.EXPO_PUBLIC_API_URL
  || 'http://localhost:4000';

// Define the background task - this must be at the top level
TaskManager.defineTask(BACKGROUND_LOCATION_TASK, async ({ data, error }) => {
  if (error) {
    return;
  }

  if (data) {
    const { locations } = data;
    const location = locations[0];

    if (location) {
      try {
        // Get the trip ID and auth token from storage
        const tripId = await AsyncStorage.getItem(STORAGE_KEY_TRIP_ID);
        const authToken = await AsyncStorage.getItem(STORAGE_KEY_AUTH_TOKEN);

        if (tripId && authToken) {
          const apiUrl = getApiUrl();

          // Send location update to the API
          await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              query: `
                mutation UpdateDriverLocation($input: UpdateDriverLocationInput!) {
                  updateDriverLocation(input: $input) {
                    id
                    latitude
                    longitude
                    timestamp
                  }
                }
              `,
              variables: {
                input: {
                  tripId: parseInt(tripId, 10),
                  latitude: location.coords.latitude,
                  longitude: location.coords.longitude,
                },
              },
            }),
          });
        }
      } catch (err) {
        // Background location update error - silently fail
      }
    }
  }
});

/**
 * Request background location permissions
 */
export const requestBackgroundPermissions = async () => {
  const { status: foregroundStatus } = await Location.requestForegroundPermissionsAsync();
  if (foregroundStatus !== 'granted') {
    return { granted: false, error: 'Foreground location permission denied' };
  }

  const { status: backgroundStatus } = await Location.requestBackgroundPermissionsAsync();
  if (backgroundStatus !== 'granted') {
    return { granted: false, error: 'Background location permission denied' };
  }

  return { granted: true };
};

/**
 * Start background location tracking for a trip
 * Auth token should already be stored by AuthContext
 */
export const startBackgroundLocationTracking = async (tripId) => {
  // Store trip ID for the background task
  await AsyncStorage.setItem(STORAGE_KEY_TRIP_ID, String(tripId));

  // Check if already tracking
  const isTracking = await Location.hasStartedLocationUpdatesAsync(BACKGROUND_LOCATION_TASK);
  if (isTracking) {
    return { success: true, alreadyTracking: true };
  }

  // Start background location updates
  await Location.startLocationUpdatesAsync(BACKGROUND_LOCATION_TASK, {
    accuracy: Location.Accuracy.High,
    timeInterval: 60000, // 60 seconds
    distanceInterval: 50, // 50 meters
    deferredUpdatesInterval: 60000,
    deferredUpdatesDistance: 50,
    showsBackgroundLocationIndicator: true,
    foregroundService: {
      notificationTitle: 'KCB - Suivi du trajet',
      notificationBody: 'Votre position est partagée avec les organisateurs',
      notificationColor: '#2563eb',
    },
    pausesUpdatesAutomatically: false,
    activityType: Location.ActivityType.AutomotiveNavigation,
  });

  return { success: true };
};

/**
 * Stop background location tracking
 */
export const stopBackgroundLocationTracking = async () => {
  const isTracking = await Location.hasStartedLocationUpdatesAsync(BACKGROUND_LOCATION_TASK);
  if (isTracking) {
    await Location.stopLocationUpdatesAsync(BACKGROUND_LOCATION_TASK);
  }

  // Clear stored trip ID
  await AsyncStorage.removeItem(STORAGE_KEY_TRIP_ID);

  return { success: true };
};

/**
 * Check if background location tracking is active
 */
export const isBackgroundLocationTrackingActive = async () => {
  try {
    return await Location.hasStartedLocationUpdatesAsync(BACKGROUND_LOCATION_TASK);
  } catch {
    return false;
  }
};

export { BACKGROUND_LOCATION_TASK };
